// Dependency-free spreadsheet exports. CSV, and Excel via the SpreadsheetML 2003
// XML format (.xls) — a genuine multi-sheet Excel file Excel/LibreOffice open
// natively, with no third-party library. No fiscal/declaration format is encoded
// here; these are plain accounting extracts.

export type Sheet = { name: string; headers: string[]; rows: (string | number)[][] };

function download(filename: string, mime: string, content: string) {
  const blob = new Blob(['﻿' + content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function csvCell(v: string | number): string {
  const s = String(v ?? '');
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function buildCsv(headers: string[], rows: (string | number)[][]): string {
  return [headers.map(csvCell).join(','), ...rows.map((r) => r.map(csvCell).join(','))].join('\r\n');
}

export function exportCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  download(filename.endsWith('.csv') ? filename : filename + '.csv', 'text/csv;charset=utf-8', buildCsv(headers, rows));
}

function xmlEscape(v: string | number): string {
  return String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function xlsCell(v: string | number): string {
  const num = typeof v === 'number' && isFinite(v);
  return `<Cell><Data ss:Type="${num ? 'Number' : 'String'}">${xmlEscape(v)}</Data></Cell>`;
}
function xlsRow(cells: (string | number)[]): string {
  return `<Row>${cells.map(xlsCell).join('')}</Row>`;
}

export function buildXls(sheets: Sheet[]): string {
  const body = sheets.map((s) => {
    const header = `<Row ss:StyleID="h">${s.headers.map((h) => `<Cell ss:StyleID="h"><Data ss:Type="String">${xmlEscape(h)}</Data></Cell>`).join('')}</Row>`;
    const rows = s.rows.map(xlsRow).join('');
    return `<Worksheet ss:Name="${xmlEscape(s.name).slice(0, 31)}"><Table>${header}${rows}</Table></Worksheet>`;
  }).join('');
  return (
    `<?xml version="1.0"?>\n<?mso-application progid="Excel.Sheet"?>\n` +
    `<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">` +
    `<Styles><Style ss:ID="h"><Font ss:Bold="1"/></Style></Styles>${body}</Workbook>`
  );
}

export function exportXls(filename: string, sheets: Sheet[]) {
  download(filename.endsWith('.xls') ? filename : filename + '.xls', 'application/vnd.ms-excel', buildXls(sheets));
}
